/**
 * Collection Page - Display a themed menu collection
 *
 * Shows items in a specific collection (e.g., Valentine's Day Menu)
 * URL: /menu/:slug (e.g., /menu/valentines)
 *
 * When a holiday kit / special menu is off or ended, show a friendly
 * "has ended" state (never hang on loading) and noindex the page.
 */

import { useState, useEffect } from 'react';
import { applySeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE } from '../utils/seoDefaults';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { publicLoadErrorMessage } from '../lib/publicSiteErrorMessage';

const API_URL = import.meta.env.VITE_API_URL || '';
/** Don't leave shoppers on a spinner if the API is slow or hangs. */
const FETCH_TIMEOUT_MS = 8000;

interface CollectionItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  original_price: number | null;
  image_url: string | null;
  category: string;
}

interface Collection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  items: CollectionItem[];
}

type LoadErrorKind = 'ended' | 'error';

export default function CollectionPage() {
  const { slug } = useParams<{ slug: string }>();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorKind, setErrorKind] = useState<LoadErrorKind | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setErrorKind('ended');
      setErrorMessage('This seasonal menu has ended. See our current menu.');
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const fetchCollection = async () => {
      setLoading(true);
      setErrorKind(null);
      setErrorMessage(null);
      setCollection(null);

      try {
        const response = await fetch(`${API_URL}/api/collections/slug/${encodeURIComponent(slug)}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 404) {
            setErrorKind('ended');
            setErrorMessage(
              'This seasonal menu has ended. See our current menu for what we are baking now.'
            );
          } else {
            setErrorKind('error');
            setErrorMessage(publicLoadErrorMessage());
          }
          return;
        }

        const data = await response.json();
        setCollection(data);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          setErrorKind('error');
          setErrorMessage('This menu is taking too long to load. Please try again, or view the full menu.');
        } else {
          console.error('Collection load:', err);
          setErrorKind('error');
          setErrorMessage(publicLoadErrorMessage());
        }
      } finally {
        window.clearTimeout(timer);
        setLoading(false);
      }
    };

    fetchCollection();
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [slug]);

  useEffect(() => {
    if (collection) {
      const description =
        collection.description ||
        `Shop the ${collection.name} collection at Nanny B's Bakery in Barrie, Ontario. Order online for pickup.`;
      applySeo({
        title: `${collection.name} | Nanny B's Bakery Menu`,
        description,
        canonicalPath: `/menu/${collection.slug}`,
        og: {
          type: 'website',
          title: `${collection.name} | Nanny B's Bakery`,
          description,
          image: collection.image_url || DEFAULT_OG_IMAGE,
        },
      });
      return;
    }

    if (errorKind === 'ended' || errorKind === 'error') {
      // Off / ended holiday kits must not stay indexed.
      applySeo({
        title: "Seasonal Menu Ended | Nanny B's Bakery",
        description:
          'This seasonal menu has ended. See our current menu at Nanny B\'s Bakery in Barrie, Ontario.',
        canonicalPath: '/menu',
        robots: 'noindex, follow',
        og: {
          type: 'website',
          title: "Seasonal Menu Ended | Nanny B's Bakery",
          description:
            'This seasonal menu has ended. See our current menu at Nanny B\'s Bakery in Barrie, Ontario.',
          image: DEFAULT_OG_IMAGE,
        },
      });
    }
  }, [collection, errorKind]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-bakery-cream to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading collection...</p>
        </div>
      </div>
    );
  }

  if (errorKind || !collection) {
    const ended = errorKind === 'ended' || !errorKind;
    return (
      <div className="min-h-screen bg-gradient-to-b from-bakery-cream to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4" aria-hidden="true">
            {ended ? '🍂' : '🍰'}
          </div>
          <h1 className="text-2xl font-display font-bold text-gray-900 mb-2">
            {ended ? 'This seasonal menu has ended' : 'Oops!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {errorMessage ||
              (ended
                ? 'See our current menu for what we are baking now.'
                : 'Something went wrong. Please try again.')}
          </p>
          <Link to="/menu" className="btn-primary">
            See our current menu
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-bakery-cream to-white min-h-screen">
      <section className="relative">
        {collection.image_url ? (
          <div className="bg-purple-50">
            <img
              src={collection.image_url}
              alt={collection.name}
              className="mx-auto max-h-[75vh] w-auto max-w-full object-contain"
            />
          </div>
        ) : null}
        <div className={collection.image_url ? 'container-custom py-8' : 'gradient-primary text-white py-12 md:py-16'}>
          <div className={collection.image_url ? '' : 'container-custom text-center'}>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-4xl md:text-5xl font-display font-bold mb-4 ${collection.image_url ? 'text-gray-900' : ''}`}
            >
              {collection.name}
            </motion.h1>
            {collection.description && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`text-lg max-w-2xl whitespace-pre-line ${collection.image_url ? 'text-gray-700' : 'text-gray-200 mx-auto text-xl'}`}
              >
                {collection.description}
              </motion.p>
            )}
          </div>
        </div>
      </section>

      <div className="container-custom py-4">
        <Link
          to="/menu"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Full Menu
        </Link>
      </div>

      <section className="section-padding pt-0">
        <div className="container-custom">
          {collection.items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No items in this collection yet.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {collection.items.map((item) => (
                <Link
                  key={item.id}
                  to={`/product/${item.id}`}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow block"
                >
                  <div className="aspect-square overflow-hidden bg-gray-100">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl">
                        🧁
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-semibold text-gray-900 mb-1">
                      {item.name}
                    </h3>
                    {item.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        {item.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-primary-600">
                        ${item.price.toFixed(2)}
                      </span>
                      {item.original_price && item.original_price > item.price && (
                        <span className="text-sm text-gray-400 line-through">
                          ${item.original_price.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-primary-50">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-display font-bold text-gray-900 mb-4">
            Ready to Order?
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Order online for pickup during open hours, or call the shop.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:7055034966" className="btn-primary">
              Call (705) 503-4966
            </a>
            <Link to="/order" className="btn-outline">
              Order Online
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
